import { useCallback, useEffect, useRef, useState } from "react";
import type { Document } from "../../types/document";
import { FileCard } from "./FileCard";
import styles from "./FileCategoryCarousel.module.css";

interface FileCategoryCarouselProps {
	documents: Document[];
	t: (key: string, options?: Record<string, string>) => string;
	onShare: (document: Document) => void;
}

export function FileCategoryCarousel({
	documents,
	t,
	onShare,
}: FileCategoryCarouselProps) {
	const scrollRef = useRef<HTMLDivElement>(null);
	const [activeIndex, setActiveIndex] = useState(0);

	const handleScroll = useCallback(() => {
		const el = scrollRef.current;
		if (!el || documents.length <= 1) return;
		// Derive the snap step from the first item's actual rendered width + the
		// flex `gap`, not `scrollWidth / length` (which folds in inter-item gaps
		// and drifts from the true step, mis-highlighting dots).
		const first = el.firstElementChild as HTMLElement | null;
		const gap = Number.parseFloat(getComputedStyle(el).columnGap) || 0;
		const step = first ? first.offsetWidth + gap : el.scrollWidth;
		const idx = step > 0 ? Math.round(el.scrollLeft / step) : 0;
		setActiveIndex(Math.min(Math.max(idx, 0), documents.length - 1));
	}, [documents.length]);

	useEffect(() => {
		const el = scrollRef.current;
		if (!el) return;
		el.addEventListener("scroll", handleScroll, { passive: true });
		return () => el.removeEventListener("scroll", handleScroll);
	}, [handleScroll]);

	// When the document set changes (e.g. search filtering), reset to the first
	// card so activeIndex can't point past the new shorter list and the carousel
	// doesn't stay scrolled to a now-invalid offset.
	const docSignature = documents.map((d) => d.id).join(",");
	// biome-ignore lint/correctness/useExhaustiveDependencies: reset is keyed on the doc-id signature, not the array identity.
	useEffect(() => {
		setActiveIndex(0);
		if (scrollRef.current) scrollRef.current.scrollLeft = 0;
	}, [docSignature]);

	if (documents.length === 1) {
		return (
			<div className={styles.singleCard}>
				<FileCard document={documents[0]} t={t} onShare={onShare} />
			</div>
		);
	}

	return (
		<div className={styles.carouselWrapper}>
			<div
				ref={scrollRef}
				className={styles.carousel}
				data-testid="file-category-carousel"
			>
				{documents.map((doc) => (
					<div key={doc.id} className={styles.carouselItem}>
						<FileCard document={doc} t={t} onShare={onShare} />
					</div>
				))}
			</div>
			<div className={styles.dots}>
				{documents.map((doc, index) => (
					<div
						key={doc.id}
						data-testid="carousel-dot"
						className={`${styles.dot} ${index === activeIndex ? styles.dotActive : styles.dotInactive}`}
					/>
				))}
			</div>
		</div>
	);
}
